import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Plus, User, ChevronDown, LogOut, LayoutDashboard, Bell, Globe, Heart, Settings } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Annunci', path: '/listings' },
  { label: 'Servizi', path: '/services' },
  { label: 'Blog', path: '/blog' },
];

const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Francais', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Espanol', flag: '🇪🇸' },
  { code: 'pt', label: 'Portugues', flag: '🇵🇹' },
  { code: 'ru', label: 'Russkij', flag: '🇷🇺' },
  { code: 'ar', label: 'Arabi', flag: '🇸🇦' },
  { code: 'zh', label: 'Zhongwen', flag: '🇨🇳' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentLang, setCurrentLang] = useState('it');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* silent */ }
  }, [user]);

  // Poll notifications every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      try {
        await axios.put(`${API}/notifications/${notif.notification_id}/read`, {}, { withCredentials: true });
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
      } catch { /* silent */ }
    }
    if (notif.link) navigate(notif.link);
  };

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-md bg-zinc-950/90 border-b border-zinc-800/50 pwa-safe-header" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <img src="/logo-header.png" alt="AeroDeals" className="h-8 w-auto object-contain" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-sm font-medium transition-colors ${
                isActive(item.path) ? 'text-[#C5A059]' : 'text-zinc-400 hover:text-white'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/create-listing"
            className="flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors"
            data-testid="create-listing-btn"
          >
            <Plus size={16} />
            Nuovo Annuncio
          </Link>

          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 px-2.5 py-2 text-zinc-400 hover:text-white transition-colors" data-testid="language-selector">
                <Globe size={16} />
                <span className="text-xs font-medium uppercase">{currentLang}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-44 max-h-72 overflow-auto" data-testid="language-dropdown">
              {LANGUAGES.map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setCurrentLang(lang.code)}
                  className={`cursor-pointer text-sm focus:bg-zinc-800 ${currentLang === lang.code ? 'text-[#C5A059]' : 'text-zinc-300 focus:text-white'}`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span className="mr-2">{lang.flag}</span> {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications Bell */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors" data-testid="notifications-bell">
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-[#C5A059] text-black text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-80 max-h-96 overflow-auto" data-testid="notifications-dropdown">
                <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                  <span className="text-sm font-medium text-white">Notifiche</span>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-[10px] text-[#C5A059] hover:underline" data-testid="mark-all-read-btn">
                      Segna tutte lette
                    </button>
                  )}
                </div>
                {notifications.length > 0 ? (
                  notifications.slice(0, 10).map(n => (
                    <DropdownMenuItem
                      key={n.notification_id}
                      onClick={() => handleNotificationClick(n)}
                      className={`cursor-pointer px-3 py-3 focus:bg-zinc-800 ${!n.read ? 'bg-zinc-800/50' : ''}`}
                      data-testid={`notification-item-${n.notification_id}`}
                    >
                      <div className="w-full">
                        <div className="flex items-center gap-2 mb-0.5">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0" />}
                          <span className={`text-xs font-medium ${!n.read ? 'text-white' : 'text-zinc-400'}`}>{n.title}</span>
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-snug">{n.body}</p>
                        <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="px-3 py-6 text-center text-xs text-zinc-600">Nessuna notifica</div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={14} />
                    )}
                  </div>
                  <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-48">
                <div className="px-3 py-2 border-b border-zinc-800">
                  <p className="text-sm font-medium text-white">{user.name}</p>
                  <p className="text-xs text-zinc-500">{user.email}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="text-zinc-300 cursor-pointer focus:bg-zinc-800 focus:text-white" data-testid="dashboard-menu-item">
                  <LayoutDashboard size={14} className="mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/favorites')} className="text-zinc-300 cursor-pointer focus:bg-zinc-800 focus:text-white" data-testid="favorites-menu-item">
                  <Heart size={14} className="mr-2" /> Preferiti
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} className="text-zinc-300 cursor-pointer focus:bg-zinc-800 focus:text-white" data-testid="settings-menu-item">
                  <Settings size={14} className="mr-2" /> Impostazioni
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={logout} className="text-red-400 cursor-pointer focus:bg-zinc-800 focus:text-red-300" data-testid="logout-menu-item">
                  <LogOut size={14} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="text-sm text-zinc-300 hover:text-white border border-zinc-700 px-5 py-2.5 hover:border-zinc-500 transition-colors"
              data-testid="login-btn"
            >
              Accedi
            </Link>
          )}
        </div>

        {/* Mobile Toggle */}
        <div className="md:hidden flex items-center gap-2">
          {user && unreadCount > 0 && (
            <button onClick={() => navigate('/dashboard')} className="relative text-zinc-400" data-testid="mobile-notification-badge">
              <Bell size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#C5A059] text-black text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>
            </button>
          )}
          <button className="text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-zinc-950 border-t border-zinc-800 px-4 py-6 space-y-4 animate-fade-in" data-testid="mobile-menu">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`block text-base font-medium ${isActive(item.path) ? 'text-[#C5A059]' : 'text-zinc-300'}`}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/create-listing" onClick={() => setMobileOpen(false)} className="block bg-[#C5A059] text-black text-center py-3 font-medium mt-4">
            Nuovo Annuncio
          </Link>
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block text-zinc-300 text-sm mt-2">
                Dashboard {unreadCount > 0 && <span className="text-[#C5A059]">({unreadCount})</span>}
              </Link>
              <Link to="/favorites" onClick={() => setMobileOpen(false)} className="block text-zinc-300 text-sm mt-2">
                Preferiti
              </Link>
              <Link to="/settings" onClick={() => setMobileOpen(false)} className="block text-zinc-300 text-sm mt-2">
                Impostazioni
              </Link>
              <button onClick={() => { logout(); setMobileOpen(false); }} className="block text-red-400 text-sm mt-2">
                Esci
              </button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-center border border-zinc-700 py-3 text-zinc-300 mt-2">
              Accedi
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
